import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Zap, Check, ArrowLeft, AlertCircle, Loader2 } from 'lucide-react';
import { metaAPI, paymentsAPI, listingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function PromoteListingPage() {
  const { listingId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [listing, setListing] = useState(null);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [tiersError, setTiersError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }

    metaAPI.getPromotionTiers()
      .then(r => {
        const data = r.data || [];
        setTiers(data);
        if (data.length > 0) setSelectedTierId(data[0].id);
      })
      .catch(() => setTiersError('Failed to load promotion tiers. Please try again.'))
      .finally(() => setTiersLoading(false));

    listingsAPI.getOne(listingId).then(r => setListing(r.data)).catch(() => navigate('/'));
  }, [user, navigate, listingId, authLoading]);

  const selectedTier = tiers.find(t => t.id === selectedTierId);

  const handleCheckout = async () => {
    if (!selectedTierId) return;
    setLoading(true);
    try {
      const { data } = await paymentsAPI.promotionCheckout({
        listing_id: listingId,
        tier_id: selectedTierId,
        origin_url: window.location.origin
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-8 sm:py-12">
        <Link to={listing ? `/listing/${listing.listing_id}` : '/browse'} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#002FA7] mb-6" data-testid="back-to-listing">
          <ArrowLeft className="w-4 h-4" /> Back to listing
        </Link>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs font-bold uppercase tracking-wider mb-4">
            <Zap className="w-3 h-3" /> Boost your listing
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-2">Promote Your Listing</h1>
          <p className="text-base text-zinc-600 max-w-lg mx-auto">Get more eyes on your listing. Featured items appear at the top of search results and on the homepage.</p>
          {listing && (
            <p className="text-sm text-zinc-500 mt-2">Promoting: <strong className="text-zinc-700">{listing.title}</strong></p>
          )}
        </div>

        {/* Loading state */}
        {tiersLoading && (
          <div className="flex flex-col items-center justify-center py-16" data-testid="tiers-loading">
            <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin mb-3" />
            <p className="text-sm text-zinc-500">Loading promotion packages...</p>
          </div>
        )}

        {/* Error state */}
        {tiersError && (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="tiers-error">
            <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
            <p className="text-sm text-red-600 mb-4">{tiersError}</p>
            <button onClick={() => window.location.reload()} className="text-sm text-[#002FA7] hover:underline">Try again</button>
          </div>
        )}

        {/* Tier Cards */}
        {!tiersLoading && !tiersError && tiers.length === 0 && (
          <div className="text-center py-16 text-zinc-500" data-testid="no-tiers">
            <p>No promotion packages available at this time.</p>
          </div>
        )}

        {!tiersLoading && !tiersError && tiers.length > 0 && (
          <>
            <div className={`grid gap-4 mb-8 ${tiers.length === 1 ? 'max-w-sm mx-auto' : tiers.length === 2 ? 'sm:grid-cols-2 max-w-2xl mx-auto' : tiers.length === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'}`} data-testid="promotion-tiers">
              {tiers.map((tier, idx) => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTierId(tier.id)}
                  className={`relative text-left p-6 rounded-lg border-2 transition-all ${selectedTierId === tier.id ? 'border-[#002FA7] bg-white shadow-lg' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}
                  data-testid={`tier-${tier.id}`}
                >
                  {idx === tiers.length - 1 && (
                    <span className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#002FA7] text-white">Best Value</span>
                  )}
                  <h3 className="font-bold text-zinc-900 mb-1">{tier.name}</h3>
                  {tier.description && <p className="text-xs text-zinc-500 mb-3">{tier.description}</p>}
                  <p className="text-2xl font-black text-zinc-900">${tier.price_usd?.toFixed(2)} <span className="text-sm font-normal text-zinc-500">USD</span></p>
                  <p className="text-sm text-zinc-500 mt-2">{tier.duration_days} day{tier.duration_days !== 1 ? 's' : ''} of exposure</p>
                  {selectedTierId === tier.id && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-[#002FA7] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Checkout */}
            <div className="bg-white rounded-lg border border-zinc-200 p-6 text-center" data-testid="checkout-section">
              <p className="text-sm text-zinc-500 mb-4">
                Selected: <strong className="text-zinc-900">{selectedTier?.name}</strong> — <strong className="text-[#002FA7]">${selectedTier?.price_usd?.toFixed(2)} USD</strong>
              </p>
              <button
                onClick={handleCheckout} disabled={loading || !selectedTierId}
                className="bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full px-8 py-3.5 font-bold text-sm disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="promote-checkout-btn"
              >
                <Zap className="w-4 h-4" /> {loading ? 'Processing...' : 'Continue to Payment'}
              </button>
              <p className="text-xs text-zinc-400 mt-3">Secure checkout powered by Stripe</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
